import { useMutation, useQuery } from "@apollo/client/react";
import {
  BookOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { App, Button, Flex, Input, Space, Table, Tooltip, Typography } from "antd";
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TableEmptyState } from "../../components/TableEmptyState.tsx";
import { tableScroll, tableSortDirections, tableSticky } from "../../layout/constants.ts";
import { DeleteRecipeDocument, ListRecipesDocument, RecipeSortField } from "../../gql/generated/graphqlSchema.tsx";
import { useSafeSearchParams } from "../../hooks/useSafeSearchParams.ts";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch.ts";
import {
  buildSearchUrl,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  tableSearchParams,
  useTableParams,
} from "../../hooks/useTableParams.tsx";
import { routes } from "../../routes.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { CreateRecipeModal } from "./CreateRecipeModal.tsx";
import { DuplicateRecipeModal } from "./DuplicateRecipeModal.tsx";
import { useTrailRoot } from "../../hooks/useTrailRoot.ts";

const columnToSortField = {
  name: RecipeSortField.Name,
  createdAt: RecipeSortField.CreatedAt,
  updatedAt: RecipeSortField.UpdatedAt,
};

const searchParamsSchema = tableSearchParams(columnToSortField).extend({
  q: z.string().optional(),
});

export const RecipesPage = () => {
  const { t, i18n } = useTranslation();
  useTrailRoot(t("recipes.title"), BookOutlined);
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<{ id: string; name: string } | null>(null);
  const { q: search, page, pageSize, sortField, sortDirection } = useSafeSearchParams(searchParamsSchema);

  const table = useTableParams({
    pageSize,
    page,
    sortField,
    sortDirection,
    columnToSortField,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, loading, refetch } = useQuery(ListRecipesDocument, {
    variables: {
      search,
      pagination: {
        offset: table.offset,
        limit: table.limit,
        sortField,
        sortDirection,
      },
    },
    fetchPolicy: "network-only",
  });

  const [deleteRecipe] = useMutation(DeleteRecipeDocument, {
    onCompleted: () => {
      void message.success(t("recipes.deletedDone"));
      void refetch();
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      void navigate(
        buildSearchUrl({
          q: value || undefined,
          pageSize: pageSize === DEFAULT_PAGE_SIZE ? undefined : String(pageSize),
          sortField,
          sortDirection,
        }),
      );
    },
    [navigate, pageSize, sortField, sortDirection],
  );
  const { onChange, onPressEnter } = useDebouncedSearch(handleSearch);

  const recipes = data?.recipes.recipes ?? [];
  const total = data?.recipes.total ?? 0;

  return (
    <Flex gap="middle" vertical>
      <Typography.Title level={3}>
        <BookOutlined /> {t("recipes.title")}
      </Typography.Title>
      <Input
        allowClear
        defaultValue={search}
        onChange={onChange}
        onPressEnter={onPressEnter}
        placeholder={t("recipes.searchPlaceholder")}
        prefix={<SearchOutlined />}
        size="large"
      />
      <Space wrap>
        <Button icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)} type="primary">
          {t("recipes.newRecipe")}
        </Button>
      </Space>
      <Table
        bordered
        columns={[
          {
            title: t("recipes.name"),
            key: "name",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("name"),
            render: (_, record) => <Link to={routes.recipeDetail.to(record.id)}>{record.name}</Link>,
          },
          {
            title: t("recipes.description"),
            key: "description",
            render: (_, record) => {
              const desc = record.description;
              return desc ? (desc.length > 80 ? `${desc.slice(0, 80)}…` : desc) : "—";
            },
          },
          {
            title: t("recipes.createdAt"),
            key: "createdAt",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("createdAt"),
            render: (_, record) => formatDateTime(record.createdAt, i18n.language),
          },
          {
            title: t("recipes.updatedAt"),
            key: "updatedAt",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("updatedAt"),
            render: (_, record) => formatDateTime(record.updatedAt, i18n.language),
          },
          {
            title: t("recipes.actions"),
            key: "actions",
            fixed: "right",
            width: 1,
            render: (_, record) => (
              <Space>
                <Tooltip destroyOnHidden title={t("recipes.duplicate")}>
                  <Button
                    disabled={!record.hasPublishedVersion}
                    icon={<CopyOutlined />}
                    onClick={() => setDuplicateTarget({ id: record.id, name: record.name })}
                    variant="outlined"
                  />
                </Tooltip>
                <Tooltip destroyOnHidden title={t("recipes.delete")}>
                  <Button
                    color="danger"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      modal.confirm({
                        title: t("recipes.deleteTitle"),
                        content: t("recipes.deleteConfirm"),
                        okButtonProps: { danger: true, icon: <DeleteOutlined /> },
                        cancelButtonProps: { icon: <CloseOutlined /> },
                        okText: t("recipes.delete"),
                        onOk: () => deleteRecipe({ variables: { id: record.id } }),
                      })
                    }
                    variant="outlined"
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]}
        dataSource={recipes}
        loading={loading}
        locale={{
          emptyText: (
            <TableEmptyState
              emptyChildren={
                <Button
                  color="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalOpen(true)}
                  variant="outlined"
                >
                  {t("recipes.newRecipe")}
                </Button>
              }
              emptyDescription={t("recipes.noRecipes")}
              loading={loading}
            />
          ),
        }}
        onChange={table.createTableChangeHandler({ q: search })}
        pagination={table.paginationConfig(total, { q: search })}
        rowKey="id"
        scroll={tableScroll}
        size="small"
        sticky={tableSticky}
      />

      <CreateRecipeModal initialName={search} onClose={() => setCreateModalOpen(false)} open={createModalOpen} />
      <DuplicateRecipeModal
        onClose={() => setDuplicateTarget(null)}
        open={duplicateTarget != null}
        recipeId={duplicateTarget?.id ?? ""}
        recipeName={duplicateTarget?.name ?? ""}
      />
    </Flex>
  );
};
